import { Component, OnInit } from "@angular/core";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { OrgBUProjectTeamStructure } from "../../../models/custom-models/org-buproject-team-structure";
import { EntityInfo } from "../../../models/custom-models/entity-info";
import { StatsService } from "../../../services/stats.service";
import { SharedService } from "../../../services/shared.service";
import { OrgBuProjectTeamStructureService } from "../../../modules/shared/Services/org-bu-project-team-structure.service";
import { OpenTaskRequestModel } from "../../../modules/shared/Models/task.model";
import { TaskService } from "../../../services/task.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UserPreferencesService } from "../../../modules/shared/Services/user-preferences.service";
import { Constants } from "../../../models/constants";
import { ViewTaskSelectionHandlingService } from "../../../modules/shared/Services/view-task-selection-handling.service";
import { DashboardService } from "../../dashboard/Services/dashboard.service";
import { SharedModule } from "../../../modules/shared/shared.module";
import { OrgBuProjectTeamStructureComponent } from "../../../modules/shared/Components/org-bu-project-team-structure/org-bu-project-team-structure.component";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { FormsModule } from "@angular/forms";
import { LoginService } from "../../../services/login.service";

@Component({
    selector: "app-get-task-no-dialog-box",
    templateUrl: "./get-task-no-dialog-box.component.html",
    styleUrls: ["./get-task-no-dialog-box.component.scss"],
    imports: [
        SharedModule,
        OrgBuProjectTeamStructureComponent,
        MatDialogModule,
        MatSelectModule,
        MatInputModule,
        MatButtonModule,
        FormsModule,
    ]
})
export class GetTaskNoDialogBoxComponent implements OnInit {
    taskNumber: number | null = null;
    dataSource = new MatTreeNestedDataSource<OrgBUProjectTeamStructure>();
    selectedEntityName: string = "";
    selectedEntityInfo: EntityInfo;
    teamCode: string | null = null;
    teamId: number | undefined;

    private destroy$ = new Subject<void>();

    constructor(
        private dialogRef: MatDialogRef<GetTaskNoDialogBoxComponent>,
        private statsService: StatsService,
        private sharedService: SharedService,
        private orgBuProjectTeamStructureService: OrgBuProjectTeamStructureService,
        private taskService: TaskService,
        private userPreferenceService: UserPreferencesService,
        private viewTaskSelectionHandlingService: ViewTaskSelectionHandlingService,
        private dashboardService: DashboardService,
        private loginService: LoginService,
    ) {
        this.getStructure();
    }

    ngOnInit(): void {
        let input = document.getElementById("taskNumInputId");
        input?.addEventListener("keypress", function (event) {
            // If the user presses the "Enter" key on the keyboard
            if (event.key === "Enter") {
                // Cancel the default action, if needed
                event.preventDefault();
                // Trigger the button element with a click
                document.getElementById("getTaskBtnId")?.click();
            }
        });
    }

    taskNumberEntered() {
        let taskNumber = this.taskNumber;
        //
        if (!taskNumber) {
            return;
        }

        if (this.teamId == 0) {
            this.dialogRef.close(taskNumber); // Close the dialog and open the Personal task
        } else {
            let openTaskRequestModel = new OpenTaskRequestModel();
            openTaskRequestModel.taskIdentifier = taskNumber;
            openTaskRequestModel.teamId = this.teamId as number;
            openTaskRequestModel.taskNumber = null;
            openTaskRequestModel.orgId = null;
            if (this.taskNumber && this.taskNumber > 0) {
                this.dialogRef.close(openTaskRequestModel);
            } else {
                console.log("Task Number is not valid");
            }
        }
    }

    getStructure() {
        if (this.loginService.isCurrentlyLoggingOut()) {
            return;
        }
        let storedResponse = this.taskService.getStoredViewTaskApiResponse();
        if (storedResponse && storedResponse.length > 0) {
            this.dataSource.data =
                this.orgBuProjectTeamStructureService.createOrgBuProjectTeamStructure(storedResponse);
            this.tryToMakeEntitySelection();
            return;
        } else {
            this.statsService
                .getOrgTeamDropdownStructureV2(this.sharedService.getLoggedInUserPrimaryEmail(), "viewtask")
                .pipe(takeUntil(this.destroy$))
                .subscribe((response) => {
                    this.sharedService.hideSpinner();
                    this.dataSource.data = this.orgBuProjectTeamStructureService.createOrgBuProjectTeamStructure(
                        response.data.organizations,
                    );
                    this.tryToMakeEntitySelection();
                });
        }
    }

    globalSelectedEntity(): OrgBUProjectTeamStructure | undefined {
        const globalEntityInfo = this.dashboardService.getSelectedTeamFromDashBoard();
        if (!globalEntityInfo) return undefined;
        // Check if the selected team from the dashboard is selectable
        return this.orgBuProjectTeamStructureService.findNodeBasedOnEntity(
            globalEntityInfo.entityId,
            Constants.EntityTypes.TEAM,
            this.dataSource.data,
        );
    }

    tryToMakeEntitySelection() {
        const globalEntityInfo = this.getClonedGlobalEntityInfo();
        if (globalEntityInfo) {
            this.setSelectedEntityInfo(globalEntityInfo);
            this.handleSelectedEntityNew(globalEntityInfo);
            return;
        }

        let localEntityInfo = this.viewTaskSelectionHandlingService.getViewTaskSelectedEntityFromLocal();
        if (localEntityInfo) {
            if (typeof localEntityInfo === "object" && localEntityInfo) {
                this.setSelectedEntityInfo(localEntityInfo);
                this.handleSelectedEntityNew(localEntityInfo);
                return;
            }
        }

        const singleSelectableEntity = this.orgBuProjectTeamStructureService.checkIsOnlyOneSelectableTeamPresent(
            this.dataSource.data,
        );
        if (singleSelectableEntity) {
            this.setSelectedEntityInfo(singleSelectableEntity);
            this.handleSelectedEntityNew(singleSelectableEntity);
            return;
        }

        this.selectTeamEntityBasedOnUserPreferences();
    }

    selectTeamEntityBasedOnUserPreferences() {
        let userPreference = this.userPreferenceService.getUserPreferencesFromStorage();
        if (userPreference) {
            if (userPreference.orgId != null) {
                let selectedEntityStructure = this.orgBuProjectTeamStructureService.findNodeBasedOnEntity(
                    userPreference.teamId,
                    Constants.EntityTypes.TEAM,
                    this.dataSource.data,
                );
                if (selectedEntityStructure) {
                    this.selectedEntityInfo = {
                        entityType: selectedEntityStructure.entityType,
                        entityName: selectedEntityStructure.name,
                        entityId: selectedEntityStructure.id,
                        entityTypeId: selectedEntityStructure.entityTypeId,
                    } as EntityInfo;
                    this.selectedEntityName = selectedEntityStructure.name;
                    this.handleSelectedEntityNew(selectedEntityStructure);
                }
            }
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    handleSelectedEntityNew(selectedNode: OrgBUProjectTeamStructure | null) {
        if (selectedNode) {
            selectedNode?.teamCode ? (this.teamCode = selectedNode.teamCode + "-") : (this.teamCode = null);
            this.teamId = selectedNode.teamId;
            this.viewTaskSelectionHandlingService.storeTaskSelectedEntityToLocal(selectedNode);
            this.setFocusToTaskNumberInput();
        } else {
            this.teamCode = null;
            this.teamId = undefined;
            this.viewTaskSelectionHandlingService.storeTaskSelectedEntityToLocal(null);
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    taskNumberKeyDownEvent($event: KeyboardEvent) {
        if ($event.key === "Enter") {
            this.taskNumberEntered();
        }
    }

    isHideGetTaskBtn() {
        return this.taskNumber == null || this.taskNumber <= 0;
    }

    private getClonedGlobalEntityInfo(): OrgBUProjectTeamStructure | undefined {
        const globalEntity = this.globalSelectedEntity();
        return globalEntity ? JSON.parse(JSON.stringify(globalEntity)) : undefined;
    }

    private setSelectedEntityInfo(entity: OrgBUProjectTeamStructure) {
        const { entityType, name, id, entityTypeId } = entity;
        this.selectedEntityInfo = { entityType, entityName: name, entityId: id, entityTypeId } as EntityInfo;
        this.selectedEntityName = name;
    }

    private setFocusToTaskNumberInput() {
        try {
            setTimeout(() => {
                let input = document.getElementById("taskNumInputId");
                if (input) {
                    input.focus();
                }
            }, 100);
        } catch (e) {
            console.log(e);
        }
    }
}
